import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ReactComponent as ArrowRight } from '../../assets/images/icons/arrow_right.svg';
import useShopPromotionOffer from '../../hooks/useShopPromotionOffer';
import * as events from '../../lib/analytics/events';
import CheckoutContext from '../../lib/CheckoutContext';
import { priceCartForPreview } from '../../lib/pricing';
import setCouponCookie from '../../lib/setCouponCookie';
import * as types from '../../types';
import styles from './HardwareFirstPromoOffer.module.scss';

/** whole percent the code takes off this cart's hardware; 0 when the server gives it nothing to take off */
function promoPercentOff(pricing: types.CartPricing): number {
  const discounts = Object.values(pricing.hardwareDiscounts ?? {});
  const listInCents = discounts.reduce((sum, discount) => sum + discount.listPriceInCents, 0);
  const promoInCents = discounts.reduce((sum, discount) => sum + discount.promoDiscountInCents, 0);
  return listInCents > 0 ? Math.round((promoInCents / listInCents) * 100) : 0;
}

/**
 * One-tap apply for the live promotion's code, under the bag's code field. The chip names the code
 * and what it takes off this cart, priced by the server with the code on, so it never promises a
 * figure the receipt will not show. Gone once the code (or any other) is on the cart.
 */
export default function HardwareFirstPromoOffer() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cart, checkoutActions } = useContext(CheckoutContext);
  const code = useShopPromotionOffer();
  const [percentOff, setPercentOff] = useState(0);

  const { cartItems, currency } = cart;
  useEffect(() => {
    if (!code) {
      setPercentOff(0);
      return;
    }
    let cancelled = false;
    priceCartForPreview({ ...cart, couponCode: code })
      .then((pricing) => {
        if (!cancelled) setPercentOff(promoPercentOff(pricing));
      })
      // already reported inside priceCartForPreview; the chip just stays away
      .catch(() => {
        if (!cancelled) setPercentOff(0);
      });
    return () => {
      cancelled = true;
    };
    // the preview follows what is in the bag, not every field of the cart object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, cartItems, currency]);

  if (!code || percentOff <= 0) {
    return null;
  }

  // the chip only shows once its own preview priced this cart with the code, so it needs no second
  // check; the cart's own pricing drops the code if it has turned invalid since
  const onApply = () => {
    dispatch(checkoutActions.addCoupon(code));
    setCouponCookie(code);
    events.cartPage.couponApplied(code);
  };

  return (
    <div className={styles.container}>
      <button className={styles.chip} onClick={onApply} type="button">
        <span className={styles.code}>{code}</span>
        <span className={styles.label}>{t('hardware_first_cart_apply_percent_off', { percent: percentOff })}</span>
        <ArrowRight className={styles.arrow} />
      </button>
    </div>
  );
}
